import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../api/users';
import { toErrorMessage } from '../api/client';
import { useStores } from '../auth/StoreContext';
import {
  ActiveBadge,
  Card,
  PageHead,
  RoleBadge,
} from '../components/ui';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { Modal } from '../components/Modal';
import type {
  Role,
  User,
  UserCreateRequest,
  UserUpdateRequest,
} from '../types';

export function UsersPage() {
  const qc = useQueryClient();
  const { stores } = useStores();
  const [filterStore, setFilterStore] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  const q = useQuery({
    queryKey: ['users', filterStore],
    queryFn: () => usersApi.list(filterStore),
  });

  const storeName = useMemo(() => {
    const map = new Map(stores.map((s) => [s.id, s.name]));
    return (id: number | null) => (id == null ? 'All stores' : map.get(id) ?? `#${id}`);
  }, [stores]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });

  return (
    <>
      <PageHead
        title="Users"
        subtitle="Provision admins and cashiers. Store isolation is enforced on the server."
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + New User
          </button>
        }
      />

      <div className="toolbar">
        <label style={{ margin: 0 }}>Store</label>
        <select
          style={{ width: 'auto', minWidth: 180 }}
          value={filterStore ?? ''}
          onChange={(e) =>
            setFilterStore(e.target.value === '' ? null : Number(e.target.value))
          }
        >
          <option value="">All stores</option>
          {stores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <Card>
        {q.isLoading ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
        ) : !q.data || q.data.length === 0 ? (
          <Empty icon="👥" title="No users found" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Full Name</th>
                  <th>Role</th>
                  <th>Store</th>
                  <th>Discount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <strong>{u.username}</strong>
                    </td>
                    <td>{u.fullName}</td>
                    <td>
                      <RoleBadge role={u.role} />
                    </td>
                    <td>{storeName(u.storeId)}</td>
                    <td>
                      {u.canApplyDiscount ? (
                        <span className="badge badge-green">Allowed</span>
                      ) : (
                        <span className="badge badge-gray">No</span>
                      )}
                    </td>
                    <td>
                      <ActiveBadge active={u.active} />
                    </td>
                    <td className="text-right">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEditing(u)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            invalidate();
          }}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}

function CreateUserModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const { stores } = useStores();
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<UserCreateRequest>({
    username: '',
    password: '',
    fullName: '',
    role: 'CASHIER',
    storeId: null,
    canApplyDiscount: false,
  });

  const set = (patch: Partial<UserCreateRequest>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const mutation = useMutation({
    mutationFn: () => usersApi.create(draft),
    onSuccess: onSaved,
    onError: (err) => setError(toErrorMessage(err)),
  });

  const needsStore = draft.role !== 'SUPER_ADMIN';

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open
      title="New User"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={
              mutation.isPending ||
              !draft.username ||
              !draft.password ||
              !draft.fullName ||
              (needsStore && draft.storeId == null)
            }
          >
            {mutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <div className="field-row">
          <div className="field">
            <label>Username *</label>
            <input
              value={draft.username}
              onChange={(e) => set({ username: e.target.value })}
              required
            />
          </div>
          <div className="field">
            <label>Full Name *</label>
            <input
              value={draft.fullName}
              onChange={(e) => set({ fullName: e.target.value })}
              required
            />
          </div>
        </div>
        <div className="field">
          <label>Password *</label>
          <input
            type="password"
            value={draft.password}
            onChange={(e) => set({ password: e.target.value })}
            required
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label>Role *</label>
            <select
              value={draft.role}
              onChange={(e) => {
                const role = e.target.value as Role;
                set({ role, storeId: role === 'SUPER_ADMIN' ? null : draft.storeId });
              }}
            >
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="STORE_ADMIN">Store Admin</option>
              <option value="CASHIER">Cashier</option>
            </select>
          </div>
          <div className="field">
            <label>Store {needsStore ? '*' : '(n/a)'}</label>
            <select
              value={draft.storeId ?? ''}
              disabled={!needsStore}
              onChange={(e) =>
                set({
                  storeId: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            >
              <option value="">
                {needsStore ? 'Select a store…' : 'All stores'}
              </option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="checkbox-row">
          <input
            id="canDiscount"
            type="checkbox"
            checked={!!draft.canApplyDiscount}
            onChange={(e) => set({ canApplyDiscount: e.target.checked })}
          />
          <label htmlFor="canDiscount">Can apply discounts</label>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: User;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<UserUpdateRequest>({
    fullName: user.fullName,
    password: '',
    canApplyDiscount: user.canApplyDiscount,
    active: user.active,
  });

  const set = (patch: Partial<UserUpdateRequest>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const mutation = useMutation({
    mutationFn: () => {
      // Only send password when it was actually entered.
      const body: UserUpdateRequest = { ...draft };
      if (!body.password) delete body.password;
      return usersApi.update(user.id, body);
    },
    onSuccess: onSaved,
    onError: (err) => setError(toErrorMessage(err)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open
      title={`Edit ${user.username}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <div className="field">
          <label>Full Name</label>
          <input
            value={draft.fullName ?? ''}
            onChange={(e) => set({ fullName: e.target.value })}
          />
        </div>
        <div className="field">
          <label>New Password</label>
          <input
            type="password"
            value={draft.password ?? ''}
            onChange={(e) => set({ password: e.target.value })}
            placeholder="Leave blank to keep current"
          />
        </div>
        <div className="checkbox-row" style={{ marginBottom: 12 }}>
          <input
            id="editDiscount"
            type="checkbox"
            checked={!!draft.canApplyDiscount}
            onChange={(e) => set({ canApplyDiscount: e.target.checked })}
          />
          <label htmlFor="editDiscount">Can apply discounts</label>
        </div>
        <div className="checkbox-row">
          <input
            id="editActive"
            type="checkbox"
            checked={!!draft.active}
            onChange={(e) => set({ active: e.target.checked })}
          />
          <label htmlFor="editActive">Active</label>
        </div>
      </form>
    </Modal>
  );
}
